<template>
  <div
    class="border-t"
    :class="darkTheme ? 'border-black' : 'border-grey400'"
  >
    <table
      v-if="facts.length"
      class="product-specs border-b w-full"
      :class="darkTheme ? 'border-black' : 'border-grey400'"
    >
      <thead
        class="cursor-pointer"
        @click="toogleExpanded('facts')"
      >
        <tr>
          <th class="py-16 md:py-24 flex items-center justify-between text-left">
            <h3 class="mb-0 pr-32 text-xsmall-medium sm:text-small-medium text-left product-overview-title">{{ productResources.facts }}</h3>
            <div class="relative h-24">
              <i class="text-lg sm:text-xl inline-block absolute right-0 top-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-16 h-16"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19.5,12h-15"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                    class="animate-all"
                    :class="{'opacity-0': expanded.includes('facts')}"
                  />
                </svg>
              </i>
            </div>
          </th>
        </tr>
      </thead>
      <transition>
        <tbody
          :class="{
            hidden: !expanded.includes('facts')
          }"
        >
          <tr
            v-for="(fact, index) in facts"
            :key="index"
            class="flex justify-between text-xsmall sm:text-small-regular mb-16 last:mb-0 last:pb-24 sm:last:pb-32"
          >
            <td class="spec-key">
              <GlobalsLinkHelper
                v-if="fact.url"
                :to="fact.url"
                class="underline hover:no-underline"
              >
                {{ fact.name }}
              </GlobalsLinkHelper>
              <span v-else>{{ fact.name }}</span>
            </td>
            <td class="text-right pl-32">
              <GlobalsLinkHelper 
                v-for="(factValue, i) in fact.values"
                :key="i"
                :to="factValue.url"
                class="underline hover:no-underline"
                :rel="(factValue.linksToVirtualPage)? 'nofollow': 'follow'"
              >
                {{ factValue.name }}<span v-if="i < (fact.values.length - 1)">,</span>
              </GlobalsLinkHelper>
            </td>
          </tr>
        </tbody>
      </transition>
    </table>
    <table
      class="product-specs border-b w-full"
      :class="darkTheme ? 'border-black' : 'border-grey400'"
    >
      <thead
        class="cursor-pointer"
        @click="toogleExpanded('properties')"
      >
        <tr>
          <th class="py-16 md:py-24 flex items-center justify-between text-left">
            <h3 class="mb-0 pr-32 text-xsmall-medium sm:text-small-medium text-left product-overview-title">{{ productResources.properties }} {{ product.name }}</h3>
            <div class="relative h-24">
              <i class="text-lg sm:text-xl inline-block absolute right-0 top-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-16 h-16"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19.5,12h-15"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                    class="animate-all"
                    :class="{'opacity-0': expanded.includes('properties')}"
                  />
                </svg>
              </i>
            </div>
          </th>
        </tr>
      </thead>
      <transition>
        <tbody
          :class="{
            hidden: !expanded.includes('properties')
          }"
        >
          <tr
            v-for="(fact, index) in product.properties"
            :key="index"
            class="flex justify-between text-xsmall sm:text-small-regular mb-16 last:mb-0"
          >
            <td class="spec-key">
              {{ fact.name }}
            </td>
            <td v-if="fact.value">{{ fact.value }}</td>
          </tr>
          <tr class="flex justify-between text-xsmall sm:text-small-regular mb-16 last:mb-0 pb-24 sm:pb-32">
            <td class="spec-key">{{ productResources.partNo }}</td>
            <td class="spec-key">{{ product.partNo }}</td>
          </tr>
        </tbody>
      </transition>
    </table>
    <div
      v-if="product.ingredients"
      class="border-b"
      :class="darkTheme ? 'border-black' : 'border-grey400'"
    >
      <div
        class="cursor-pointer py-16 flex items-center justify-between md:py-24"
        @click="toogleExpanded('ingredients')"
      >
        <h3 class="mb-0 pr-32 text-xsmall-medium sm:text-small-medium product-overview-title">{{ productResources.ingredients }}</h3>
        <div class="relative h-24">
          <i class="text-lg sm:text-xl inline-block absolute right-0 top-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-16 h-16"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19.5,12h-15"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
                class="animate-all"
                :class="{'opacity-0': expanded.includes('ingredients')}"
              />
            </svg>
          </i>
        </div>
      </div>
      <transition>
        <div
          class="pb-24 sm:pb-32"
          :class="{
            hidden: !expanded.includes('ingredients')
          }"
        >
          <div class="text-xsmall sm:text-small-regular" v-html="product.ingredients" />
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type IProductFact, type IProduct } from '~/api-types';
import { type Ref, ref } from 'vue';
import { useGlobalContentStore } from '~/store/globalContent';
const globalContentStore = useGlobalContentStore();
const productResources = globalContentStore.productResources;

defineProps<{
  product: IProduct,
  facts: IProductFact[],
  darkTheme?: boolean
}>();

const expanded: Ref<string[]> = ref([]);

const toogleExpanded = (index: string) => {
  if (expanded.value.includes(index)) {
    expanded.value = expanded.value.filter(i => i !== index);
  } else {
    expanded.value.push(index);
  }
};
</script>
